import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Globe, Heart, Star, PlayCircle } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import SectionAccents from "@/components/SectionAccents";

const TRUST_ICONS = { "100% Handmade": Heart, "Premium Quality": Sparkles, "Worldwide Shipping": Globe, "Secure Payments": ShieldCheck };

const HowStep = ({ n, title, desc, delay }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay }}
        className="relative p-6 md:p-8 rounded-3xl glass border border-white/40">
        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold mb-4">{n}</div>
        <h3 className="font-display text-xl font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground">{desc}</p>
    </motion.div>
);

const Home = () => {
    const { settings } = useSettings();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const homepage = settings?.homepage;
    // Only admin-uploaded hero images — no stock/preview fallbacks.
    const heroImages = (homepage?.hero_images || []).filter(Boolean).slice(0, 6);

    useEffect(() => {
        (async () => {
            try {
                const [p, c] = await Promise.all([
                    api.get("/products", { params: { featured: true, limit: 8 } }),
                    api.get("/categories"),
                ]);
                setProducts(p.data.products);
                setCategories(c.data.categories);
            } catch {}
        })();
    }, []);

    return (
        <div>
            {/* HERO */}
            <section className="relative pt-8 pb-20 md:pt-16 md:pb-32 overflow-hidden" data-testid="hero-section">
                <SectionAccents variant="hero" />
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative z-10">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs font-medium text-primary mb-6">
                            <Sparkles className="w-3.5 h-3.5" /> Custom 3D Miniatures · Handcrafted in India
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02]">
                            {homepage?.hero_headline?.split(" ").slice(0, -2).join(" ") || "Turn Your Memories"}
                            <br />
                            <span className="relative inline-block">
                                Into <span className="text-primary">Miniatures</span>
                                <svg viewBox="0 0 200 12" className="absolute -bottom-1 left-0 w-full h-2 text-brand-gold" preserveAspectRatio="none">
                                    <path d="M0 8 Q 50 0 100 6 T 200 4" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" />
                                </svg>
                            </span>
                            <span className="text-4xl md:text-5xl lg:text-6xl">.</span>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
                            {homepage?.hero_subheading || "Custom handcrafted 3D miniatures made from your favorite moments."}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/create">
                                <Button data-testid="hero-cta-primary" className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground text-base">
                                    Order Your Miniature <ArrowRight className="w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/shop">
                                <Button data-testid="hero-cta-secondary" variant="outline" className="rounded-full h-12 px-7 border-primary/30 text-primary hover:bg-primary/5 text-base">
                                    <PlayCircle className="w-4 h-4" /> {homepage?.hero_cta_secondary || "Browse Collection"}
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {(homepage?.trust_badges || Object.keys(TRUST_ICONS)).map((b) => {
                                const Icon = TRUST_ICONS[b] || ShieldCheck;
                                return (
                                    <div key={b} className="glass rounded-2xl px-3 py-2.5 flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary"><Icon className="w-4 h-4" /></div>
                                        <span className="text-xs font-semibold leading-tight">{b}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>

                    {/* Floating gallery */}
                    <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
                        className="relative h-[520px] md:h-[620px]">
                        <div className="absolute inset-0">
                            <div className="ambient-orb bg-brand-peach top-6 left-8 w-64 h-64 opacity-70" />
                            <div className="ambient-orb bg-brand-lavender bottom-6 right-8 w-72 h-72 opacity-70" />
                        </div>
                        {heroImages[0] && (
                        <div className="absolute top-4 right-8 w-52 h-64 rounded-4xl overflow-hidden shadow-2xl animate-float-slow">
                            <img src={heroImages[0]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        {heroImages[1] && (
                        <div className="absolute bottom-6 left-2 w-44 h-56 rounded-4xl overflow-hidden shadow-2xl animate-float" style={{ animationDelay: "1.5s" }}>
                            <img src={heroImages[1]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        {heroImages[2] && (
                        <div className="absolute top-40 left-28 w-40 h-52 rounded-4xl overflow-hidden shadow-2xl animate-float-slower" style={{ animationDelay: "0.8s" }}>
                            <img src={heroImages[2]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        {/* NEW tile — small top-left */}
                        {heroImages[3] && (
                        <div className="absolute top-2 left-4 w-28 h-28 rounded-3xl overflow-hidden shadow-xl animate-float-slower ring-2 ring-white/70" style={{ animationDelay: "2.2s" }}>
                            <img src={heroImages[3]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        {/* NEW tile — mid right small */}
                        {heroImages[4] && (
                        <div className="absolute top-72 right-2 w-32 h-32 rounded-3xl overflow-hidden shadow-xl animate-float ring-2 ring-white/70" style={{ animationDelay: "1.1s" }}>
                            <img src={heroImages[4]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        {/* NEW tile — bottom right small */}
                        {heroImages[5] && (
                        <div className="absolute bottom-24 right-32 w-24 h-24 rounded-2xl overflow-hidden shadow-xl animate-float-slow ring-2 ring-white/70" style={{ animationDelay: "0.4s" }}>
                            <img src={heroImages[5]} alt="miniature" className="w-full h-full object-cover" />
                        </div>
                        )}
                        <div className="absolute -bottom-4 right-14 glass-strong rounded-2xl px-4 py-3 flex items-center gap-3 animate-float">
                            <div className="w-10 h-10 rounded-full bg-brand-gold text-primary-foreground flex items-center justify-center"><Star className="w-5 h-5" /></div>
                            <div>
                                <div className="font-bold text-sm">4.9 · 5,000+ reviews</div>
                                <div className="text-[11px] text-muted-foreground">India's favourite miniature brand</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* SHOP BY CATEGORY — removed per user request */}

            {/* BESTSELLERS */}
            <section className="max-w-7xl mx-auto px-6 py-14" data-testid="featured-products-section">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Handpicked</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Bestsellers</h2>
                    </div>
                    <Link to="/shop" className="text-sm text-primary hidden md:inline">Shop all →</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative py-20 overflow-hidden" data-testid="how-it-works-section">
                <SectionAccents variant="craft" />
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">A few clicks. Forever.</div>
                        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">How it works</h2>
                        <p className="text-muted-foreground mt-3">From your favourite photos to a hand-painted miniature on your desk in as little as 12 days.</p>
                    </div>
                    <div className="grid md:grid-cols-4 gap-4">
                        <HowStep n="1" title="Pick a style" desc="Choose a category, product and the size that feels just right." delay={0} />
                        <HowStep n="2" title="Upload photos" desc="Send us a few reference images — front, side, and any details." delay={0.1} />
                        <HowStep n="3" title="Handcraft" desc="Our sculptors hand-craft your miniature from your photos and share progress updates." delay={0.2} />
                        <HowStep n="4" title="Approve & delight" desc="We handcraft and ship your masterpiece with premium packaging." delay={0.3} />
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="relative max-w-7xl mx-auto px-6 py-14 overflow-hidden" data-testid="testimonials-section">
                <SectionAccents variant="sweet" />
                <div className="grid md:grid-cols-3 gap-4 relative z-10">
                    {[
                        { name: "Ananya S.", city: "Mumbai", text: "The likeness is uncanny. Everyone asked where I got it — perfect anniversary gift.", stars: 5 },
                        { name: "Rohit K.", city: "Bangalore", text: "The 3D preview was fun. My family's mini is now sitting in our living room and we love it.", stars: 5 },
                        { name: "Priya M.", city: "Delhi", text: "Craftsmanship is top-notch. Packaging felt like unboxing an Apple product. 💛", stars: 5 },
                    ].map((t) => (
                        <div key={t.name} className="rounded-3xl glass p-6">
                            <div className="flex gap-0.5 mb-3">{[...Array(t.stars)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-gold text-brand-gold" />)}</div>
                            <p className="text-sm leading-relaxed">"{t.text}"</p>
                            <div className="mt-4 text-sm font-semibold">{t.name} <span className="text-muted-foreground font-normal">· {t.city}</span></div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="relative rounded-4xl bg-gradient-to-br from-primary to-primary/70 p-10 md:p-16 text-primary-foreground overflow-hidden">
                    <div className="ambient-orb bg-brand-gold -top-24 -right-24 w-80 h-80 opacity-50" />
                    <div className="relative z-10 max-w-2xl">
                        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Ready to hold your memory in your hands?</h2>
                        <p className="mt-3 text-primary-foreground/85 text-lg">Start your miniature today — a keepsake that lasts a lifetime.</p>
                        <Link to="/shop" className="inline-flex mt-6">
                            <Button className="rounded-full h-12 px-7 bg-white text-primary hover:bg-white/90 text-base" data-testid="bottom-cta">
                                Start creating <ArrowRight className="w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
